import { useState, useEffect, useRef } from 'react';
import { ownerService, propertyService } from 'services/api';
import { useAuth } from 'context/AuthContext';

function groupByProperty(owners) {
  const map = {};
  owners.forEach(o => {
    const units = o.units || [];
    if (units.length === 0) {
      const key = 'sin-propiedad';
      if (!map[key]) map[key] = { id: key, name: 'Sin propiedad asignada', owners: [], property: null };
      map[key].owners.push({ ...o, _units: [] });
      return;
    }
    const propMap = {};
    units.forEach(u => {
      const propId   = u.building?.property?.id   || 'sin-propiedad';
      const propName = u.building?.property?.name || 'Sin propiedad asignada';
      if (!propMap[propId]) propMap[propId] = { id: propId, name: propName, property: u.building?.property || null, units: [] };
      propMap[propId].units.push(u);
    });
    Object.values(propMap).forEach(({ id, name, property, units: propUnits }) => {
      if (!map[id]) map[id] = { id, name, property, owners: [] };
      map[id].owners.push({ ...o, _units: propUnits });
    });
  });
  return Object.values(map).filter(g => g.id !== 'sin-propiedad').concat(
    map['sin-propiedad'] ? [map['sin-propiedad']] : []
  );
}

// Lógica de la pantalla de Propietarios: lista, filtros, importación y quitar vínculo.
export default function useOwners() {
  const { isSuperAdmin, user, getSubscriptionStateForProperty } = useAuth();
  const isAdminMultiProp = user?.role === 'admin';

  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [page,      setPage]      = useState(1);
  const [total,     setTotal]     = useState(0);
  const [alert,     setAlert]     = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting,   setDeleting]   = useState(false);
  const [delError,   setDelError]   = useState('');
  const [editando,   setEditando]   = useState(null);   // propietario cuyos datos se corrigen
  const [cambiando,  setCambiando]  = useState(null);   // { owner, unidades } del propietario cuya unidad se traspasa

  const [file,            setFile]            = useState(null);
  const [importing,       setImporting]       = useState(false);
  const [importResult,    setImportResult]    = useState(null);
  const [showPropWarning, setShowPropWarning] = useState(false);
  const fileRef = useRef();

  const [importProp,      setImportProp]      = useState('');
  const [importPropObj,   setImportPropObj]   = useState(null);
  const [filterProp,      setFilterProp]      = useState('');
  const [sinProp,         setSinProp]         = useState(false); // super admin: solo propietarios sin ninguna unidad
  const [selectedPropObj, setSelectedPropObj] = useState(null);
  const [collapsed,       setCollapsed]       = useState({});
  const toggleGroup = (id) => setCollapsed(c => ({ ...c, [id]: !c[id] }));

  // Para admin multi-prop: cargar propiedades para calcular suscripción de la seleccionada
  const [allProperties, setAllProperties] = useState([]);
  const [propsLoaded,    setPropsLoaded]    = useState(false);
  useEffect(() => {
    if (isSuperAdmin || isAdminMultiProp) {
      propertyService.selector({ limit: 200 }).then(r => {
        const props = r.data.data || [];
        setAllProperties(props);
        // Admin con 1 propiedad → auto-seleccionarla para validar membresía
        if (isAdminMultiProp && !isSuperAdmin && props.length === 1) {
          setFilterProp(String(props[0].id));
          setSelectedPropObj(props[0]);
          setImportProp(String(props[0].id));
          setImportPropObj(props[0]);
        }
        setPropsLoaded(true);
      }).catch(() => setPropsLoaded(true));
    }
  }, [isSuperAdmin, isAdminMultiProp]);

  // Estado de suscripción de la propiedad seleccionada (lista)
  const effectiveSub = selectedPropObj ? getSubscriptionStateForProperty(selectedPropObj) : 'active';
  const isReadOnly   = effectiveSub === 'grace';
  const isBlocked    = effectiveSub === 'blocked';

  // Estado de suscripción de la propiedad de importación
  const importSub       = importPropObj ? getSubscriptionStateForProperty(importPropObj) : 'active';
  const importBlocked   = importSub === 'blocked';

  const LIMIT = 50;

  // Super admin y admin con varias propiedades: la lista se ve al ELEGIR una propiedad (como en
  // Unidades, Torres y Asistentes). Sin propiedad, solo se busca si escriben en el buscador (búsqueda
  // global). Un admin con una sola propiedad la tiene elegida desde que cargan sus propiedades.
  const necesitaPropiedad = (isSuperAdmin || (isAdminMultiProp && propsLoaded && allProperties.length > 1))
    && !filterProp && !sinProp && !search.trim();

  const load = (p = page, s = search, propId = filterProp, sin = sinProp) => {
    // Super admin sin propiedad ni texto: no se pide la lista global (15 000+ propietarios).
    if (isSuperAdmin && !propId && !sin && !(s || '').trim()) { setItems([]); setTotal(0); setLoading(false); return; }
    setLoading(true);
    ownerService.getAll({ page: p, limit: LIMIT, search: s, ...(propId ? { property_id: propId } : (sin ? { sin_propiedad: 1 } : {})) })
      .then(r => {
        setItems(r.data.data || []);
        setTotal(r.data.total || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(page, search); }, [page]); // eslint-disable-line
  // Recargar del servidor al cambiar la propiedad filtrada — antes el filtro
  // era solo del lado del cliente sobre la lista global de propietarios, así
  // que un propietario con unidades en dos propiedades traía las unidades de
  // ambas en vez de solo la que se estaba filtrando.
  useEffect(() => { load(1, search, filterProp); }, [filterProp]); // eslint-disable-line
  // Activar/desactivar «Sin propiedad asignada» (la primera vez, al montar, no hace nada).
  const sinPropMontado = useRef(false);
  useEffect(() => {
    if (!sinPropMontado.current) { sinPropMontado.current = true; return; }
    setPage(1); load(1, search, filterProp, sinProp);
  }, [sinProp]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, search); }, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const handleImport = async () => {
    if (!file) return setAlert({ type: 'danger', message: 'Selecciona un archivo Excel' });
    // Mismas reglas que el backend (ownersController: solo .xlsx/.xls, 5 MB):
    // se avisan antes de subir el archivo en vez de esperar el error del servidor.
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) return setAlert({ type: 'danger', message: 'Solo se permiten archivos Excel (.xlsx, .xls)' });
    if (file.size > 5 * 1024 * 1024) return setAlert({ type: 'danger', message: 'El archivo supera el tamaño máximo de 5 MB' });
    // Sin propiedad destino no se importa nada, para ningún rol — antes solo
    // se bloqueaba a super_admin; un admin con varias propiedades podía
    // mandar el import sin seleccionar destino y el backend caía al
    // property_id del propio Excel.
    if ((isSuperAdmin || isAdminMultiProp) && !importProp) { setShowPropWarning(true); return; }
    setAlert(null); setImporting(true); setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if ((isSuperAdmin || isAdminMultiProp) && importProp) formData.append('property_id_override', importProp);
      const r = await ownerService.import(formData);
      setImportResult(r.data);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      load(1, ''); setPage(1);
    } catch (err) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Error al importar' });
    } finally { setImporting(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const base  = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const res   = await fetch(`${base}/owners/template`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = 'plantilla_propietarios.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { setAlert({ type: 'danger', message: 'No se pudo descargar la plantilla' }); }
  };

  // El reporte ya viene en base64 dentro de la respuesta del import — no hace
  // falta otra llamada al backend, solo decodificarlo a un archivo descargable.
  const handleDownloadReport = () => {
    if (!importResult?.reporte_excel) return;
    const byteChars  = atob(importResult.reporte_excel);
    const byteArray  = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = importResult.reporte_excel_filename || 'reporte_import.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  // "Eliminar" un propietario quita su vínculo con las unidades de ESTA
  // propiedad puntual — nunca borra a la persona (puede tener unidades en
  // otras propiedades). Solo tiene sentido dentro de un grupo con propiedad
  // real (group.id !== 'sin-propiedad'), porque el backend exige property_id.
  const handleDeleteOwner = (owner, group) => setConfirmDel({ owner, group });

  const handleEditarPropietario = (owner) => setEditando(owner);
  const terminarEdicion = (message) => {
    setAlert({ type: 'success', message });
    setEditando(null);
    load(page, search, filterProp);
  };
  const handleCambiarPropietario = (owner, unidades) => setCambiando({ owner, unidades });
  const terminarCambio = (message) => {
    setAlert({ type: 'success', message });
    setCambiando(null);
    load(page, search, filterProp);
  };

  const confirmDeleteOwner = async () => {
    if (!confirmDel) return;
    const { owner, group } = confirmDel;
    setDeleting(true);
    setDelError('');
    try {
      const r = await ownerService.delete(owner.id, group.id);
      setAlert({ type: 'success', message: r.data.message });
      setConfirmDel(null);
      load(page, search, filterProp);
    } catch (err) {
      setDelError(err.response?.data?.message || 'No se pudo quitar al propietario. Inténtalo de nuevo.');
    } finally {
      setDeleting(false);
    }
  };

  // Al cambiar el filtro de propiedad, guardar el objeto completo
  const handleFilterProp = (propId) => {
    setFilterProp(propId);
    if (propId) setSinProp(false);
    // Buscar el objeto en los items cargados o en allProperties
    const propObj = allProperties.find(p => String(p.id) === String(propId)) || null;
    setSelectedPropObj(propObj);
  };

  // El backend ya filtra por property_id y solo devuelve, por propietario,
  // las unidades de la propiedad filtrada — no hace falta volver a filtrar
  // acá (antes esto era client-side sobre la lista global, por eso un
  // propietario con unidades en dos propiedades mostraba ambas).
  const grouped = (isSuperAdmin || filterProp) ? groupByProperty(items) : [];

  return {
    LIMIT, alert, allProperties, cambiando, collapsed, editando, confirmDel, confirmDeleteOwner, delError, deleting,
    fileRef, filterProp, grouped, handleCambiarPropietario, handleEditarPropietario, handleDeleteOwner, handleDownloadReport, handleDownloadTemplate,
    handleFilterProp, handleImport, importBlocked, importProp, importResult, importing,
    isAdminMultiProp, isBlocked, isReadOnly, isSuperAdmin, items, loading, necesitaPropiedad, page,
    propsLoaded, search, setAlert, setCambiando, setEditando, setConfirmDel, setDelError, setFile, setFilterProp, setImportProp,
    setImportPropObj, setImportResult, setPage, setSearch, setSelectedPropObj, setShowPropWarning,
    setSinProp, showPropWarning, sinProp, terminarCambio, terminarEdicion, toggleGroup, total,
  };
}
